// Semantic-decision colour helpers for Owner Dashboard KPIs + attention severity.
// Pure mapping — does NOT change formulas, counts, labels, ordering, or API shape.
//
// Executive Color System (2026-02-27) — values aligned with the
// CertOpz "improved dashboard" target palette:
//   • Blue   #2563EB · Primary KPI accent (Invoice Revenue, navigation accents)
//   • Green  #16A34A · Healthy / paid / approved / positive
//   • Cyan   #0EA5E9 · Informational accent (Gross Margin)
//   • Teal   #14B8A6 · Process / TAT
//   • Amber  #F59E0B · Outstanding / waiting / medium risk
//   • Red    #DC2626 · Blocked / overdue / SLA breach / failed
//   • Slate  #64748B · Neutral / muted / secondary labels
// All four financial KPI cards now carry a canonical accent regardless
// of dynamic health (the trend chip already conveys directional health).

export const COLORS = {
  successGreen: "#16A34A",
  activeBlue: "#2563EB",
  infoCyan: "#0EA5E9",
  processTeal: "#14B8A6",
  warningAmber: "#F59E0B",
  riskOrange: "#F97316",
  criticalRed: "#DC2626",
  compliancePurple: "#7C3AED",
  financeIndigo: "#4F46E5",
  neutralSlate: "#64748B",
  milkWhite: "#FFFFFF",
  softTrack: "#F1F5F9",
  cardBorder: "#E5E7EB",
  // Legacy aliases (kept stable for consumers across the app)
  green: "#16A34A",
  blue: "#2563EB",
  teal: "#14B8A6",
  cyan: "#0EA5E9",
  amber: "#F59E0B",
  orange: "#F97316",
  red: "#DC2626",
  slate: "#64748B",
  muted: "#94A3B8",
  track: "#F1F5F9",
};

// ---- KPI tone resolver ----------------------------------------------------
// Returns the CANONICAL accent colour for each Financial KPI card. Per the
// 2026-02-27 executive-color spec, each KPI carries one accent regardless
// of the health value — directional health is conveyed via TrendChip.
//
// metricKey: "revenue" | "collections" | "outstanding_ar" | "outstanding" | "gross_margin" | "margin"
// (the remaining args are accepted for backward-compat with existing call sites)
export function getFinancialKpiTone(metricKey /* , value, target, trend, extra */) {
  const key = (metricKey || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (key === "revenue")                                  return _tone("blue");
  if (key === "collections" || key === "cash")            return _tone("green");
  if (key === "outstanding_ar" || key === "outstanding" || key === "ar")
                                                          return _tone("amber");
  if (key === "gross_margin" || key === "margin")         return _tone("cyan");
  return _tone("slate");
}

function _tone(name) {
  return {
    name,
    color: COLORS[name] || COLORS.slate,
    accent: COLORS[name] || COLORS.slate,
    bg: _bg(name),
    text: _text(name),
  };
}
function _bg(name) {
  return ({
    green: "bg-emerald-50",
    blue: "bg-sky-50",
    cyan: "bg-sky-50",
    teal: "bg-teal-50",
    amber: "bg-amber-50",
    orange: "bg-orange-50",
    red: "bg-rose-50",
    slate: "bg-slate-50",
    muted: "bg-slate-50",
  })[name] || "bg-slate-50";
}
function _text(name) {
  return ({
    green: "text-emerald-700",
    blue: "text-sky-700",
    cyan: "text-sky-700",
    teal: "text-teal-700",
    amber: "text-amber-700",
    orange: "text-orange-700",
    red: "text-rose-700",
    slate: "text-slate-700",
    muted: "text-slate-500",
  })[name] || "text-slate-700";
}

// ---- Attention severity --------------------------------------------------
// Returns "red" | "orange" | "amber" | "muted"
export function getAttentionSeverity(key, value, extra = {}) {
  const k = (key || "").toLowerCase();
  const v = Number(value || 0);

  if (k.includes("sla_breach") || k.includes("payment_blocked")) {
    return v > 0 ? "red" : "muted";
  }
  if (k.includes("override")) {
    if (v === 0) return "muted";
    if (v <= 5) return "amber";
    return "orange";
  }
  if (k.includes("quote") && k.includes("limit")) {
    return v > 0 ? "amber" : "muted";
  }
  if (k.includes("docs") || k.includes("documents")) {
    if (v === 0) return "muted";
    const severe = !!extra.severeOverdue;
    if (severe) return "red";
    return v > 5 ? "orange" : "amber";
  }

  // Generic
  if (v === 0) return "muted";
  if (v >= 10) return "red";
  if (v >= 3) return "amber";
  return "amber";
}

export const ATTENTION_PALETTE = {
  red:    { dot: "bg-rose-500",   value: "text-rose-700",   ring: "ring-rose-200" },
  orange: { dot: "bg-orange-500", value: "text-orange-700", ring: "ring-orange-200" },
  amber:  { dot: "bg-amber-500",  value: "text-amber-700",  ring: "ring-amber-200" },
  muted:  { dot: "bg-slate-300",  value: "text-slate-500",  ring: "ring-slate-100" },
};

// ---- Aging bucket colours ------------------------------------------------
// 2026-09-06 · Sprint DASH-CMD-CENTRE Slice 0 — matches on the STABLE
// canonical bucket key (`core/analytics_contracts.AGING_KEYS`) rather
// than the display label. The previous substring matcher assumed
// hyphens; the six canonical labels use hyphens intentionally but the
// key is contract, the label is decoration. Six-bucket ramp:
//   CURRENT (not yet due)   → slate  (neutral, healthy)
//   D1_30    (1-30 days)    → green
//   D31_60   (31-60 days)   → blue
//   D61_90   (61-90 days)   → amber
//   D91_180  (91-180 days)  → orange
//   D180_PLUS (180+ days)   → red
const AGING_BUCKET_COLOR_MAP = {
  CURRENT:   COLORS.slate,
  D1_30:     COLORS.green,
  D31_60:    COLORS.blue,
  D61_90:    COLORS.amber,
  D91_180:   COLORS.orange || COLORS.red,   // orange isn't in COLORS today; fall through
  D180_PLUS: COLORS.red,
};
export function getAgingBucketColor(bucket) {
  // Accept either a raw bucket key string, or a payload object with `.bucket`.
  const key = typeof bucket === "string"
    ? bucket
    : (bucket?.bucket || bucket?.label || "");
  if (AGING_BUCKET_COLOR_MAP[key]) return AGING_BUCKET_COLOR_MAP[key];
  // ─── Legacy fallback: label substrings from the pre-Slice-0 dialects.
  // Kept so a mid-deploy inconsistency (stale backend, new frontend or
  // vice-versa) still renders coloured. Match on HYPHEN only — the
  // canonical labels use U+002D. Remove after two release cycles.
  const b = String(key).toLowerCase();
  if (b.startsWith("0") || b.includes("0-30") || b.includes("0_30")) return COLORS.green;
  if (b.includes("31") || b.includes("31-60") || b.includes("31_60")) return COLORS.blue;
  if (b.includes("61") || b.includes("61-90") || b.includes("61_90")) return COLORS.amber;
  if (b.includes("90+") || b.includes("90_plus") || b.startsWith("90")) return COLORS.red;
  return COLORS.slate;
}

// ---- Status colours ------------------------------------------------------
export function getStatusColor(status) {
  const s = (status || "").toString().toLowerCase();
  if (/(paid|approved|active|healthy|won|complete|completed|closed|resolved|delivered)/.test(s)) return COLORS.green;
  if (/(pending|review|in_progress|inprogress|onboarding|investigating|negotiation|submitted|sent)/.test(s)) return COLORS.amber;
  if (/(at_risk|warning|degraded|delayed|partial|overdue)/.test(s)) return COLORS.orange;
  if (/(failed|blocked|breach|critical|churn|churned|lost|rejected|cancelled|suspended)/.test(s)) return COLORS.red;
  return COLORS.slate;
}

// ---- Blocked-reason badge colours ----------------------------------------
// Risk & SLA → "Blocked · by reason" chips. Per the 2026-02-27 spec the
// chip colour now reflects the REASON, not the severity bucket.
//   • Client Documents → amber
//   • Clarification    → blue
//   • Other            → grey
// Returns the small-badge class triplet so the call site can keep its
// minimal JSX (no extra utility imports needed).
export function getBlockedReasonBadge(reason) {
  const r = (reason || "").toString().toLowerCase();
  if (r.includes("doc") || r.includes("checklist") || r.includes("upload"))
    return { dot: "bg-amber-500", chip: "bg-amber-50 text-amber-800 border border-amber-200" };
  if (r.includes("clarif") || r.includes("question") || r.includes("info"))
    return { dot: "bg-sky-500",   chip: "bg-sky-50   text-sky-800   border border-sky-200" };
  return   { dot: "bg-slate-400", chip: "bg-slate-50 text-slate-700 border border-slate-200" };
}
